/**
 * PlaybookContextPane - what this playbook IS, readable from any tab.
 *
 * Categories, the source document and the rule counts used to live in the
 * Details tab only, so the moment someone opened Clause Rules the answer to
 * "which agreement types is this playbook for, and what was it extracted
 * from" left the screen. They are a pane now: docked, resizable, remembered.
 *
 * It is a pane BODY, so it draws no card and no header of its own - the
 * `Panel` around it already has both. See `shared/surface/PaneBody`.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiDownloadSimpleLight,
  PiEyeLight,
} from 'react-icons/pi';
import Badge from '../../shared/TailwindComponents/Badge';
import Button from '../../shared/TailwindComponents/Button';
import { Field } from '../../shared/brand/Field';
import { formatDateTime } from '../../../utils/functions';
import { PlaybookDetail } from '../../../types/playbook';
import { FileIcon } from '../../shared/attachments';
import {
  playbookSourceDocuments,
  playbookSourceDocumentCount,
} from '../playbookSourceDocuments';

interface Props {
  playbook: PlaybookDetail;
  /** Streams the source document into the global file reader. */
  onViewSource: () => void;
  onDownloadSource: () => void;
  isStreamingSource?: boolean;
  /** Jump the canvas to a rules tab. */
  onOpenTab: (tab: 'Clause Rules' | 'Global Rules' | 'Reviews') => void;
}

const CountRow: React.FC<{ label: string; value: number; onClick: () => void }> = ({
  label, value, onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex items-center justify-between px-3 py-2 rounded-lg
               bg-surface-sunken/70 border border-hairline-soft
               hover:bg-surface transition-colors duration-200 ease-slide"
  >
    <span className="text-xs text-fg-body">{label}</span>
    <span className="text-sm font-medium text-fg-strong">{value}</span>
  </button>
);

const PlaybookContextPane: React.FC<Props> = ({
  playbook,
  onViewSource,
  onDownloadSource,
  isStreamingSource,
  onOpenTab,
}) => {
  const { t } = useTranslation();
  const sourceDocuments = playbookSourceDocuments(playbook);
  const sourceCount = playbookSourceDocumentCount(playbook);

  return (
    <div className="p-3 space-y-4">
      <div className="space-y-2">
        <CountRow
          label={t('Clause Rules')}
          value={playbook.clause_rules?.length || 0}
          onClick={() => onOpenTab('Clause Rules')}
        />
        <CountRow
          label={t('Global Rules')}
          value={playbook.global_rules?.length || 0}
          onClick={() => onOpenTab('Global Rules')}
        />
        <CountRow
          label={t('Review Sessions')}
          value={playbook.recent_reviews?.length || 0}
          onClick={() => onOpenTab('Reviews')}
        />
      </div>

      <Field label={t('Agreement Types')}>
        {playbook.agreement_types?.length ? (
          <div className="flex flex-wrap gap-1">
            {playbook.agreement_types.map((type, i) => (
              <Badge key={i} color="blue" className="capitalize">{type}</Badge>
            ))}
          </div>
        ) : null}
      </Field>

      <Field label={t('Jurisdictions')}>
        {playbook.jurisdictions?.length ? (
          <div className="flex flex-wrap gap-1">
            {playbook.jurisdictions.map((j, i) => (
              <Badge key={i} color="purple" className="capitalize">{j}</Badge>
            ))}
          </div>
        ) : null}
      </Field>

      <Field label={sourceCount > 1 ? t('Source Documents ({{count}})', { count: sourceCount }) : t('Source Document')}>
        {sourceDocuments.length ? (
          <div className="space-y-2">
            {/*
              The pane is narrow, so it names the documents rather than listing
              them with controls: the first two by name, then "+N more". The
              full list with per-document View/Download lives in the Details
              tab. The buttons below act on the first document - for the
              single-document playbooks that is still every playbook created
              before provenance, they are the whole story.
            */}
            <div className="space-y-1">
              {sourceDocuments.slice(0, 2).map((doc, i) => (
                <div key={doc.uuid || i} className="flex items-center gap-2 min-w-0">
                  <FileIcon name={doc.file_name || undefined} mime={doc.file_type || undefined} className="w-4 h-4 shrink-0" />
                  <span className="text-sm text-fg-body truncate" title={doc.file_name || undefined}>
                    {doc.file_name || t('Untitled file')}
                  </span>
                </div>
              ))}
              {sourceDocuments.length > 2 && (
                <p className="text-xs text-fg-muted ps-6">
                  {t('+{{count}} more', { count: sourceDocuments.length - 2 })}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="small"
                onClick={onViewSource}
                icon={<PiEyeLight className="w-4 h-4" />}
                disabled={isStreamingSource}
                isLoading={isStreamingSource}
              >
                {t('View')}
              </Button>
              <Button
                variant="outline"
                size="small"
                onClick={onDownloadSource}
                icon={<PiDownloadSimpleLight className="w-4 h-4" />}
              >
                {t('Download')}
              </Button>
            </div>
          </div>
        ) : null}
      </Field>

      <Field label={t('Last Updated')} value={playbook.updated_at ? formatDateTime(playbook.updated_at) : ''} />
      <Field label={t('Created')} value={playbook.created_at ? formatDateTime(playbook.created_at) : ''} />
    </div>
  );
};

export default PlaybookContextPane;
